import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { ElementProps } from '@/components/sections/builder/types';

import { cn, isEmpty } from '@/lib/utils';
import { useParams, useSearchParams } from 'next/navigation';
import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ICollection } from '@/types/collection';
import CardProductBuilder from '@/components/sections/builder/elements/body/card-product';
import CardProductSkeleton from '@/components/sections/card-loading';
import { useProductCollections } from '@/hooks/api/storefront/product-collection';

import { CollectionTabElementDataType } from '@/components/sections/builder/elements/body/collection-tab/type';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ProductGridElementDataType } from '@/components/sections/builder/elements/body/collection-product-grid/type';
import { ProductListElementDataType } from '@/components/sections/builder/elements/body/product-grid/type';

interface ChildData {
  collection: ICollection;
  name: string;
}

interface ChildElement {
  id: string;
  type: string;
  group: string;
  data: ChildData;

  childs: any[];
  name: string;
  parentId: string;
}

export default function ElementContentColectionTab({
  element,
  preview,
  theme,
  host,
}: ElementProps) {
  const query = useSearchParams();
  const params = useParams();
  const themeId = query.get('theme_id') || '';
  const token = query.get('token') || '';
  const slug = params.slug || '';
  const data = element.data as CollectionTabElementDataType;
  const childs = (element.childs || []) as ChildElement[];

  const [activeTab, setActiveTab] = useState(childs[0]?.id || '');

  const activeChild = childs.find((child) => child.id === activeTab);
  const activeCollectionSlug = activeChild?.data?.collection?.slug || '';

  const queryParams = {
    slug: activeCollectionSlug,
    hostname: host as string,
    page: 1,
    pageSize: data.limit as number,
    limit: data.limit,
  };

  const defaultValue = childs[0]?.id || '';

  const { data: productData, isLoading } = useProductCollections(queryParams, token as string, {
    enabled: !!activeCollectionSlug,
  });

  const products = productData?.data || [];

  const handleResetFilter = () => {};
  const collectionAdminDesc = productData?.description;

  return (
    <>
      <section
        className={cn(
          'background-color',
          data.background?.type === 'color' && 'background-color',
          data.background?.type === 'image' && 'background-image'
        )}
      >
        <div className='theme_layout_page_width mx-auto duration-100'>
          <div className='collection-list-padding relative'>
            {data.overlay && <div className={cn('overlay absolute inset-0')} />}
            <div className='relative p-3'>
              <div
                className={cn(
                  'flex flex-col',
                  data.contentAlign === 'left' && '[&>*]:text-left',
                  data.contentAlign === 'center' && '[&>*]:text-center',
                  data.contentAlign === 'right' && '[&>*]:text-right'
                )}
              >
                <div className={cn('mb-4', data.positionViewAll === 'top' && 'w-full')}>
                  <div
                    className={cn(
                      'flex items-center gap-2 md:gap-3 xl:gap-4',
                      data.contentAlign === 'left' && 'justify-between',
                      data.contentAlign === 'center' && 'justify-center',
                      data.contentAlign === 'right' && 'flex-row-reverse justify-between'
                    )}
                  >
                    <h2
                      className={cn(
                        'theme_font_heading text-color-primary pt-5 text-base font-bold tracking-tight md:pt-0 lg:pt-0',
                        data.headingSize === 'large' &&
                          'text-xl sm:text-2xl md:text-3xl lg:text-4xl',
                        data.headingSize === 'medium' &&
                          'text-lg sm:text-xl md:text-2xl lg:text-3xl',
                        data.headingSize === 'small' &&
                          'text-base sm:text-lg md:text-xl lg:text-2xl'
                      )}
                      data-animation='true'
                      dangerouslySetInnerHTML={{ __html: data.headingLabel as string }}
                    />

                    {data.isShowViewAll && (
                      <Link
                        href={`/collection-list${
                          token
                            ? `?theme_id=${themeId}&token=${token}`
                            : preview
                            ? `?theme_id=${themeId}&isPreview=true`
                            : ''
                        }`}
                        className={cn(data.positionViewAll === 'bottom' && 'hidden')}
                      >
                        <Button
                          variant={data.viewAllStyle as 'default'}
                          className={cn(
                            'theme_font_heading text-sm font-bold tracking-tight sm:text-base',
                            data.viewAllStyle === 'link' && 'p-0'
                          )}
                          style={
                            !isEmpty(data.colorSchemeCollectionList) &&
                            data.viewAllStyle === 'default'
                              ? {
                                  backgroundColor:
                                    data.colorSchemeCollectionList?.btnSolidBackgroud,
                                  color: data?.colorSchemeCollectionList?.btnSolidlabel,
                                }
                              : data.viewAllStyle === 'link'
                              ? {
                                  color: data.colorSchemeCollectionList?.btnSolidlabel,
                                }
                              : {}
                          }
                        >
                          {data.labelViewAll}
                        </Button>
                      </Link>
                    )}
                  </div>

                  {data.showAdminCollectionDesc ||
                    (data.textLabel && (
                      <div
                        className={cn(
                          'minimal-tiptap-editor tiptap ProseMirror text-color-secondary ',
                          data.textStyle === 'body' && 'text-base md:text-lg lg:text-xl',
                          data.textStyle === 'subtitle' && 'text-sm md:text-base lg:text-lg',
                          data.textStyle === 'uppercase' &&
                            'text-xs uppercase md:text-sm lg:text-base'
                        )}
                        data-animation='true'
                        dangerouslySetInnerHTML={{
                          __html: data.showAdminCollectionDesc
                            ? (collectionAdminDesc as string)
                            : (data.textLabel as string),
                        }}
                      />
                    ))}
                </div>

                <div
                  className={cn(
                    `theme_layout_space_grid_horizontal theme_layout_space_grid_vertical desktop-grid-col w-full place-content-center items-stretch duration-300 ease-in-out`
                  )}
                >
                  <div className='hidden w-full md:block'>
                    <Tabs
                      defaultValue={defaultValue}
                      className='w-full '
                      onValueChange={(e) => setActiveTab(e)}
                    >
                      <div className='w-full overflow-x-auto overflow-y-hidden scrollbar-hide'>
                        <TabsList className='flex w-auto min-w-full flex-nowrap'>
                          {childs.map((child) => {
                            const sameSlugs = childs.filter(
                              (c) => c.data?.collection?.slug === child.data?.collection?.slug
                            );
                            const currentIndex = sameSlugs.findIndex((c) => c.id === child.id);
                            const displayName =
                              sameSlugs.length > 1 && currentIndex > 0
                                ? `${child.data?.collection?.collection_name} 
                               `
                                : child.data?.collection?.collection_name || 'Collection';

                            return (
                              <TabsTrigger
                                key={child.id}
                                value={child.id}
                                className={cn(
                                  `inline-flex flex-shrink-0 whitespace-nowrap px-4 py-2 text-sm sm:text-base`
                                )}
                                style={{
                                  color:
                                    activeTab === child.id
                                      ? data.colorSchemeCollectionList?.text
                                      : data.colorSchemeCollectionList?.textSecondary,
                                  borderBottomColor:
                                    activeTab === child.id
                                      ? data.colorSchemeCollectionList?.text
                                      : 'transparent',
                                }}
                                isCustom
                              >
                                {displayName}
                              </TabsTrigger>
                            );
                          })}
                        </TabsList>
                      </div>

                      {childs.map((child) => (
                        <TabsContent key={child.id} value={child.id} className='mt-4 w-full'>
                          {activeTab === child.id && (
                            <>
                              {isLoading ? (
                                <div className='grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-4'>
                                  {Array.from({ length: 4 }).map((_, index) => (
                                    <CardProductSkeleton key={index} />
                                  ))}
                                </div>
                              ) : products.length > 0 ? (
                                <div
                                  className={cn(
                                    `product-list-padding grid-cols-${data.totalColMobile} sm:grid-cols-${data.totalCol} md:grid-cols-${data.totalCol} lg:grid-cols-${data.totalCol} theme_layout_space_grid_horizontal theme_layout_space_grid_vertical grid h-full w-full place-content-center items-stretch`,
                                    (data.fullWidth ||
                                      data.background?.backgroundLayout === 'full_width') &&
                                      'full_width_product_list',
                                    data.size === 'small' &&
                                      '[&>article>div>div>div]:text-xs [&>article>div>div>h2]:text-base [&>article>div>div>p]:text-xs [&>article>div>div>span]:text-xs',
                                    data.size === 'medium' &&
                                      '[&>article>div>div>div]:text-sm [&>article>div>div>h2]:text-lg [&>article>div>div>p]:text-sm [&>article>div>div>span]:text-sm',
                                    data.size === 'large' &&
                                      '[&>article>div>div>div]:text-sm [&>article>div>div>h2]:text-lg [&>article>div>div>p]:text-sm [&>article>div>div>span]:text-sm'
                                  )}
                                >
                                  {products.map((product) => (
                                    <CardProductBuilder
                                      data={
                                        data as
                                          | ProductListElementDataType
                                          | ProductGridElementDataType
                                      }
                                      product={product}
                                      key={product.id}
                                      element={element}
                                      preview={preview}
                                      theme={theme}
                                    />
                                  ))}
                                </div>
                              ) : (
                                <div className='w-full flex-col text-center'>
                                  <h2 className='my-16 text-base font-light text-black lg:text-2xl'>
                                    No products found <br />
                                    Use fewer filters or
                                    <Button
                                      variant='link'
                                      className='-ml-3 text-base font-light underline hover:decoration-[3px] lg:text-2xl'
                                      onClick={handleResetFilter}
                                    >
                                      remove all
                                    </Button>
                                  </h2>
                                </div>
                              )}
                            </>
                          )}
                        </TabsContent>
                      ))}
                    </Tabs>
                  </div>
                </div>

                <div className='block w-full md:hidden'>
                  <Select value={activeTab} onValueChange={setActiveTab}>
                    <SelectTrigger
                      className='w-full'
                      style={{
                        backgroundColor: data.colorSchemeCollectionList?.background || '#f9fafb',
                        color: data.colorSchemeCollectionList?.text || '#374151',
                        borderColor: data.colorSchemeCollectionList?.text || '#d1d5db',
                      }}
                    >
                      <SelectValue placeholder='Select Category' />
                    </SelectTrigger>
                    <SelectContent className='max-h-[10rem] overflow-y-scroll'>
                      {childs.map((child) => {
                        const sameSlugs = childs.filter(
                          (c) => c.data?.collection?.slug === child.data?.collection?.slug
                        );
                        const currentIndex = sameSlugs.findIndex((c) => c.id === child.id);
                        const displayName =
                          sameSlugs.length > 1 && currentIndex > 0
                            ? `${child.data?.collection?.collection_name} `
                            : child.data?.collection?.collection_name || 'Collection';

                        return (
                          <SelectItem
                            key={child.id}
                            value={child.id}
                            style={{
                              color: data.colorSchemeCollectionList?.text || '#374151',
                            }}
                          >
                            {displayName}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>

                  {/* Mobile Product Content */}
                  <div className='mt-4'>
                    {/* Hanya tampilkan loading dan data jika ada activeTab */}
                    {activeTab && (
                      <>
                        {isLoading ? (
                          <div className='grid grid-cols-2 gap-3'>
                            {Array.from({ length: 4 }).map((_, index) => (
                              <CardProductSkeleton key={index} />
                            ))}
                          </div>
                        ) : products.length > 0 ? (
                          <div
                            className={cn(
                              `theme_layout_space_grid_horizontal theme_layout_space_grid_vertical grid h-full w-full place-content-center`,
                              products.length > 0
                                ? `grid-cols-${data.totalColMobile || 2} md:grid-cols-${
                                    data.totalCol || 4
                                  } lg:grid-cols-${data.totalCol || 4}`
                                : 'flex'
                            )}
                          >
                            {products.map((product) => (
                              <CardProductBuilder
                                data={
                                  data as ProductListElementDataType | ProductGridElementDataType
                                }
                                product={product}
                                key={product.id}
                                element={element}
                                preview={preview}
                                theme={theme}
                              />
                            ))}
                          </div>
                        ) : (
                          <div className='w-full flex-col text-center'>
                            <h2 className='my-16 text-base font-light text-black'>
                              No products found <br />
                              Use fewer filters or{' '}
                              <Button
                                variant='link'
                                className='-ml-3 text-base font-light underline hover:decoration-[3px]'
                                onClick={handleResetFilter}
                              >
                                remove all
                              </Button>
                            </h2>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {data.isShowViewAll && (
                  <div
                    className={cn(
                      'mt-5 flex justify-center sm:mt-6 lg:mt-7 xl:mt-8',
                      data.positionViewAll === 'top' && 'hidden'
                    )}
                  >
                    <Link
                      href={`/collection-list${
                        token
                          ? `?theme_id=${themeId}&token=${token}&slug=${slug}`
                          : preview
                          ? `?theme_id=${themeId}&isPreview=true&slug=${slug}`
                          : ''
                      }`}
                    >
                      <Button
                        variant={data.viewAllStyle as 'default'}
                        className={cn(
                          'theme_font_heading mx-auto mt-4 flex w-fit items-center px-4 text-sm tracking-tight sm:text-base',
                          data.viewAllStyle === 'link' && 'button-link-scheme p-0'
                        )}
                        style={
                          !isEmpty(data.colorSchemeCollectionList) &&
                          data.viewAllStyle === 'default'
                            ? {
                                backgroundColor: data.colorSchemeCollectionList?.btnSolidBackgroud,
                                color: data?.colorSchemeCollectionList?.btnSolidlabel,
                              }
                            : {}
                        }
                      >
                        {data.labelViewAll}
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <style jsx global>
        {`
          .flex_basis_${element.id} {
            flex-basis: ${data.totalColMobile === 2 ? '50%' : '100%'} !important;
          }
        `}
      </style>
      <style jsx>
        {`
          @media (min-width: 1024px) {
            .desktop-grid-col {
              grid-template-columns: repeat(${data.totalCol || 1}, minmax(0, 1fr));
            }
          }
          @media (min-width: 768px) {
            .collection-list-padding {
              padding-top: ${data.padding?.top || 0}px;
              padding-bottom: ${data.padding?.bottom || 0}px;
              padding-left: ${data.padding?.left || 0}px;
              padding-right: ${data.padding?.right || 0}px;
            }
          }
          .overlay {
            background-color: ${data.overlay};
          }
          .text-color-primary {
            color: ${data.colorSchemeCollectionList?.text};
          }
          .text-color-secondary {
            color: ${data.colorSchemeCollectionList?.textSecondary};
          }
          .button-link-scheme {
            color: ${data.colorSchemeCollectionList?.btnSolidlabel};
          }
          .background-color {
            background-color: ${data.background?.backgroundColor
              ? data.background?.backgroundColor
              : data.colorSchemeCollectionList?.background};
            --tw-bg-opacity: ${data.background?.backgroundOpacity};
          }
          .background-image {
            background-image: url(${data.background?.backgroundImage});
            background-repeat: no-repeat;
            background-size: ${data.background?.backgroundSize};
            background-position: ${data.background?.backgroundPosition};
            background-attachment: ${data.background?.backgroundAttachment};
          }
        `}
      </style>
    </>
  );
}
